import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Campaign, CampaignStatus } from '@neuroedge/shared';
import { Button, Card, PageState } from '../../components/ui';
import {
  DataGrid,
  applyClientGrid,
  useGridState,
  type ClientColumnSpec,
  type GridColumn,
} from '../../components/data-grid/DataGrid';
import { useCampaigns } from '../../api/campaigns';

const STATUSES: { value: CampaignStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'SENDING', label: 'Sending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

type Tab = 'ALL' | CampaignStatus;

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

function progress(c: Campaign) {
  if (c.totalRecipients === 0) return '—';
  const done = c.sentCount + c.failedCount + c.skippedCount;
  return (
    <>
      {done}/{c.totalRecipients}
      <div style={muted}>
        {c.sentCount} sent · {c.readCount ?? 0} read · {c.failedCount} failed · {c.skippedCount}{' '}
        skipped
      </div>
    </>
  );
}

const SPEC: Record<string, ClientColumnSpec<Campaign>> = {
  name: { text: (c) => c.name },
  status: { sort: (c) => STATUSES.findIndex((s) => s.value === c.status) },
  totalRecipients: { number: (c) => c.totalRecipients },
  sentCount: { number: (c) => c.sentCount },
  createdAt: { date: (c) => c.createdAt },
};

/**
 * WhatsApp marketing campaigns on the standard grid - status tabs with
 * counts, name/recipients/sent/created filters, sorting and paging (in the
 * browser: a tenant has tens of campaigns, not thousands). Creating,
 * recipients, starting and cancelling happen on `CampaignFormPage`.
 */
export function CampaignsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const { data, isError, error, refetch, isFetching } = useCampaigns({ pageSize: 1000 });

  const all = data?.items ?? [];
  const inTab = tab === 'ALL' ? all : all.filter((c) => c.status === tab);
  const page = applyClientGrid(inTab, grid, SPEC);

  const columns: GridColumn<Campaign>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      filter: { type: 'text' },
      render: (c) => <Link to={`/campaigns/${c.id}`}>{c.name}</Link>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <>
          <span className="ne-badge">{c.status}</span>
          {c.pausedAt ? (
            <div style={{ ...muted, color: 'var(--color-danger)' }}>Paused</div>
          ) : c.holdUntil && new Date(c.holdUntil).getTime() > Date.now() ? (
            <div style={muted}>
              {c.holdReason === 'DAILY_LIMIT' ? 'Daily limit reached' : 'Waiting'} - continues{' '}
              {new Date(c.holdUntil).toLocaleString()}
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'totalRecipients',
      header: 'Recipients',
      sortable: true,
      align: 'right',
      filter: { type: 'range' },
      render: (c) => c.totalRecipients,
    },
    {
      key: 'sentCount',
      header: 'Progress',
      sortable: true,
      filter: { type: 'range' },
      render: progress,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (c) => new Date(c.createdAt).toLocaleDateString(),
    },
  ];

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'ALL', label: 'All', count: all.length },
    ...STATUSES.map((s) => ({
      id: s.value as Tab,
      label: s.label,
      count: all.filter((c) => c.status === s.value).length,
    })),
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Campaigns</h1>
        <span style={{ flex: 1 }} />
        <Button onClick={() => navigate('/campaigns/new')}>New campaign</Button>
      </div>

      <Card>
        <div role="tablist" aria-label="Campaign status" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {data ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          {isError ? (
            <PageState kind="error" message={error.message} onRetry={() => void refetch()} />
          ) : (
            <DataGrid
              columns={columns}
              rows={page.rows}
              rowKey={(c) => c.id}
              rowLabel={(c) => c.name}
              total={page.total}
              state={grid}
              onStateChange={setGrid}
              loading={isFetching}
              emptyMessage={
                all.length === 0
                  ? 'No campaigns yet - create your first WhatsApp marketing campaign.'
                  : 'No campaigns match these filters.'
              }
            />
          )}
        </div>
      </Card>
    </div>
  );
}
